import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';
import {
  Button,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  TextField,
  Typography,
  InputLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  FormControl,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import {
  createApplicant,
  fetchManagerForApplicant,
  updateApplicant,
} from 'api';
import messages from 'messages';
import { SnackbarVariant } from '../../../constants';
import { Autocomplete } from '@material-ui/lab';

const DEFAULT_APPLICANT = {
  socialSecurityNumber: '',
  email: '',
  title: '',
  validityPeriodStartDate: '',
  validityPeriodExpiryDate: '',
  applicantsLicenseExpiryDate: '',
  sectionIds: [],
};

export default function ApplicantForm({
  applicant,
  isEditing,
  handleClose,
  updateListCallback,
  sectionManagerlist,
  section,
  titleOptions = [],
}) {
  const currentApplicant = applicant ? applicant : DEFAULT_APPLICANT;
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [formError, setFormError] = useState({
    socialSecurityNumber: false,
    email: false,
  });
  const { sectionId } = useParams();

  const [managerSelectorOpen, setManagerSelectorOpen] = useState(false);
  const managers = sectionManagerlist;
  const [selectedManager, setSelectedManager] = useState(null);
  const IS_STUDENT_SECTION =
    section && section.size > 0 && section[0].externalType.includes('5');

  const [formState, setFormState] = useState({
    id: currentApplicant.id,
    socialSecurityNumber: currentApplicant.socialSecurityNumber,
    hasEmail:
      currentApplicant.email !== null && currentApplicant.email !== ''
        ? 'true'
        : 'false',
    email: currentApplicant.email,
    givenName: currentApplicant.givenName,
    surName: currentApplicant.surName,
    title: currentApplicant.title || '',
    firstName: currentApplicant.firstName,
    validityPeriodStartDate: currentApplicant.validityPeriodStartDate,
    validityPeriodExpiryDate: currentApplicant.validityPeriodExpiryDate,
    applicantsLicenseExpiryDate: currentApplicant.applicantsLicenseExpiryDate,
    sectionId: sectionId,
  });

  useEffect(() => {
    if (
      !formError.socialSecurityNumber &&
      !formError.email &&
      formState.socialSecurityNumber !== '' &&
      formState.hasEmail !== null &&
      !(formState.hasEmail === 'true' && formState.email === '') &&
      (!IS_STUDENT_SECTION || formState.givenName !== '') &&
      (!IS_STUDENT_SECTION || formState.surName !== '') &&
      (!IS_STUDENT_SECTION || formState.firstName !== '') &&
      !formError.validityPeriodStartDate &&
      !formError.validityPeriodExpiryDate &&
      !formError.applicantsLicenseExpiryDate
    ) {
      setDisableSubmit(false);
    } else {
      setDisableSubmit(true);
    }
  }, [
    formState,
    formError,
    sectionManagerlist,
    selectedManager,
    IS_STUDENT_SECTION,
  ]);

  useEffect(() => {
    if (selectedManager === null) {
      if (formState.sectionId && formState.id) {
        fetchManagerForApplicant(formState.sectionId, formState.id)
          .then((response) => {
            setSelectedManager(
              sectionManagerlist.find((f) => response.data.id === f.id)
            );
          })
          .catch(() => {
            setSelectedManager({ firstName: '', lastName: '', id: -1 });
          });
      }
    }
  }, [selectedManager, formState, sectionManagerlist]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState({ ...formState, [name]: value });

    switch (name) {
      case 'socialSecurityNumber':
        setFormState({ ...formState, [name]: value.toUpperCase() });
        setFormError({
          ...formError,
          [name]:
            !/^[0-9]{6}[Aa-][0-9]{3}[A-FHJ-NPR-TU-Ya-fhj-npr-tu-y0-9]$/.test(
              value
            ),
        });
        break;
      case 'email':
        setFormError({
          ...formError,
          [name]: !/.+@.+\.[A-Za-z]+$/.test(value),
        });
        break;
      case 'hasEmail':
        setFormError({
          ...formError,
          email:
            value === 'true'
              ? !/.+@.+\.[A-Za-z]+$/.test(formState.email)
              : false,
        });
        break;
      case 'validityPeriodStartDate':
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const startDate = new Date(value);
        const endDate = new Date(formState.validityPeriodExpiryDate);
        const diffInDays = Math.ceil(
          (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        setFormError({
          ...formError,
          validityPeriodStartDate: startDate < yesterday,
          validityPeriodExpiryDate: endDate < startDate || diffInDays > 395,
        });
        break;
      case 'validityPeriodExpiryDate':
        const validityStartDate = new Date(formState.validityPeriodStartDate);
        const expiryDate = new Date(value);
        const daysDiff = Math.ceil(
          (expiryDate.getTime() - validityStartDate.getTime()) /
            (1000 * 60 * 60 * 24)
        );

        setFormError({
          ...formError,
          validityPeriodExpiryDate:
            expiryDate < validityStartDate || daysDiff > 395,
          applicantsLicenseExpiryDate:
            new Date(formState.applicantsLicenseExpiryDate) > expiryDate,
        });
        break;
      case 'applicantsLicenseExpiryDate':
        const licenseExpiryDate = new Date(value);
        const validityExpiryDate = new Date(formState.validityPeriodExpiryDate);

        setFormError({
          ...formError,
          applicantsLicenseExpiryDate:
            licenseExpiryDate < new Date(formState.validityPeriodStartDate) ||
            licenseExpiryDate > validityExpiryDate,
        });
        break;
      default:
        break;
    }
  };

  const editApplicant = () => {
    if (!disableSubmit) {
      const formattedDates = {
        validityPeriodStartDate: new Date(
          formState.validityPeriodStartDate
        ).toLocaleDateString('en-GB'),
        validityPeriodExpiryDate: new Date(
          formState.validityPeriodExpiryDate
        ).toLocaleDateString('en-GB'),
        applicantsLicenseExpiryDate: new Date(
          formState.applicantsLicenseExpiryDate
        ).toLocaleDateString('en-GB'),
      };

      const newFormState = {
        ...formState,
        ...formattedDates,
        socialSecurityNumber: formState.socialSecurityNumber.trim(),
        hasEmail: formState.hasEmail === 'true',
        email: formState.email.trim(),
      };

      updateApplicant(newFormState, formState.id, selectedManager)
        .then(() => {
          enqueueSnackbar(intl.formatMessage(messages.general_save_success), {
            variant: SnackbarVariant.SUCCESS,
          });
          updateListCallback();
          handleClose();
        })
        .catch(() => {
          enqueueSnackbar(intl.formatMessage(messages.general_error), {
            variant: SnackbarVariant.ERROR,
          });
        });
    }
  };

  const createNewApplicant = () => {
    if (!disableSubmit) {
      const formattedDates = {
        validityPeriodStartDate: new Date(
          formState.validityPeriodStartDate
        ).toLocaleDateString('en-GB'),
        validityPeriodExpiryDate: new Date(
          formState.validityPeriodExpiryDate
        ).toLocaleDateString('en-GB'),
        applicantsLicenseExpiryDate: new Date(
          formState.applicantsLicenseExpiryDate
        ).toLocaleDateString('en-GB'),
      };

      const newFormState = { ...formState, ...formattedDates };

      createApplicant(newFormState, selectedManager)
        .then(() => {
          enqueueSnackbar(intl.formatMessage(messages.general_save_success), {
            variant: SnackbarVariant.SUCCESS,
          });
          updateListCallback();
          handleClose();
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 409) {
              enqueueSnackbar(
                intl.formatMessage(messages.applicant_duplicate),
                {
                  variant: SnackbarVariant.ERROR,
                }
              );
            } else {
              enqueueSnackbar(intl.formatMessage(messages.general_error), {
                variant: SnackbarVariant.ERROR,
              });
            }
          }
        });
    }
  };
  const dialogContentText = isEditing
    ? messages.applicant_edit
    : messages.applicant_create;

  const actionButtonOnClick = isEditing ? editApplicant : createNewApplicant;

  const selectedTitleOption =
    formState.title !== ''
      ? titleOptions.find((option) => option.code === formState.title) || {
          code: formState.title,
          label: formState.title,
        }
      : null;

  const titleDropdownOptions =
    selectedTitleOption !== null &&
    !titleOptions.some((option) => option.code === selectedTitleOption.code)
      ? [selectedTitleOption, ...titleOptions]
      : titleOptions;

  return (
    <>
      <DialogTitle id="confirm-dialog-title">
        <Typography component="h3">
          {intl.formatMessage(messages.applicant)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-description">
          <Typography component="subtitle2">
            {intl.formatMessage(dialogContentText)}
            <br />
            <br />
            {intl.formatMessage(messages.applicant_extra_text)}
          </Typography>
        </DialogContentText>
        <form>
          <TextField
            variant="standard"
            required
            name="socialSecurityNumber"
            label="Henkilötunnus"
            fullWidth
            value={formState.socialSecurityNumber}
            onChange={handleChange}
            inputProps={{ maxLength: 11 }}
            error={formError.socialSecurityNumber}
            helperText={
              formError.socialSecurityNumber ? 'Tarkista henkilötunnus' : ''
            }
          />
          <br />
          <br />
          <FormControl component="fieldset">
            <FormLabel component="legend">
              Onko käyttäjällä työsähköpostiosoite?
            </FormLabel>
            <RadioGroup
              aria-label="has-email"
              name="hasEmail"
              value={formState.hasEmail}
              onChange={handleChange}
            >
              {' '}
              <FormControlLabel
                value={'true'}
                control={<Radio />}
                label="Kyllä"
              />
              <FormControlLabel
                value={'false'}
                control={<Radio />}
                label="Ei"
              />
            </RadioGroup>
          </FormControl>
          {formState.hasEmail === 'true' && (
            <TextField
              variant="standard"
              required
              name="email"
              label="Sähköposti"
              fullWidth
              value={formState.email}
              onChange={handleChange}
              error={formError.email}
              helperText={formError.email ? 'Tarkista sähköposti' : ''}
            />
          )}
          <TextField
            variant="standard"
            required={IS_STUDENT_SECTION}
            name="givenName"
            label="Etunimi"
            fullWidth
            value={formState.givenName}
            onChange={handleChange}
          />
          <TextField
            variant="standard"
            required={IS_STUDENT_SECTION}
            name="surName"
            label="Sukunimi"
            fullWidth
            value={formState.surName}
            onChange={handleChange}
          />
          <>
            <InputLabel style={{ fontSize: '12px' }}>Nimike</InputLabel>
            <Autocomplete
              id="applicant-title"
              options={titleDropdownOptions}
              value={selectedTitleOption}
              getOptionLabel={(option) => option?.label || ''}
              getOptionSelected={(option, value) => option.code === value.code}
              fullWidth
              onChange={(event, newValue) => {
                setFormState({ ...formState, title: newValue?.code || '' });
              }}
              renderInput={(params) => (
                <TextField {...params} variant="standard" />
              )}
            />
          </>
          <TextField
            variant="standard"
            required={IS_STUDENT_SECTION}
            name="firstName"
            label="Kutsumanimi"
            fullWidth
            value={formState.firstName}
            onChange={handleChange}
          />
          <>
            <InputLabel style={{ fontSize: '12px' }}>Manageri</InputLabel>
            <Autocomplete
              open={managerSelectorOpen}
              value={selectedManager}
              id="manager-selector"
              options={managers}
              getOptionLabel={(option) =>
                option.firstName + ' ' + option.lastName
              }
              fullWidth
              onOpen={() => setManagerSelectorOpen(true)}
              onClose={() => setManagerSelectorOpen(false)}
              onChange={(event, newValue) => {
                setSelectedManager(
                  newValue !== null
                    ? newValue
                    : { firstName: '', lastName: '', id: -1 }
                );
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </>
          <TextField
            variant="standard"
            name="validityPeriodStartDate"
            label="Tunnuksen voimassaoloajan alkupäivä"
            type="date"
            format="dd/MM/yyyy"
            fullWidth
            value={formState.validityPeriodStartDate}
            onChange={handleChange}
            error={formError.validityPeriodStartDate}
            helperText={
              formError.validityPeriodStartDate ? 'Tarkista päivämäärät' : ''
            }
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            variant="standard"
            name="validityPeriodExpiryDate"
            label="Tunnuksen voimassoloajan loppupäivä, max 13kk alkupäivästä"
            type="date"
            format="dd/MM/yyyy"
            fullWidth
            value={formState.validityPeriodExpiryDate}
            onChange={handleChange}
            error={formError.validityPeriodExpiryDate}
            helperText={
              formError.validityPeriodExpiryDate ? 'Tarkista päivämäärät' : ''
            }
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            variant="standard"
            name="applicantsLicenseExpiryDate"
            label="Tunnusta haettava viimeistään"
            type="date"
            format="dd/MM/yyyy"
            fullWidth
            value={formState.applicantsLicenseExpiryDate}
            onChange={handleChange}
            error={formError.applicantsLicenseExpiryDate}
            helperText={
              formError.applicantsLicenseExpiryDate
                ? 'Tarkista päivämäärät'
                : ''
            }
            InputLabelProps={{
              shrink: true,
            }}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {intl.formatMessage(messages.back)}
        </Button>
        <Button
          disabled={disableSubmit}
          variant="contained"
          size="small"
          color="primary"
          onClick={actionButtonOnClick}
        >
          <FormattedMessage defaultMessage="Tallenna" />
        </Button>
      </DialogActions>
    </>
  );
}
